// Command palette (Cmd+K / Ctrl+K) using cmdk.
// Provides quick navigation, actions, and search across the Djayma admin.

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Users,
  CreditCard,
  Truck,
  BarChart3,
  Settings,
  Plus,
  Search,
  FileText,
  Heart,
  Megaphone,
  Building2,
  Sparkles,
  DollarSign,
  ScrollText,
  ShieldCheck,
  MapPin,
  Eye,
  AlertTriangle,
  Cog,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CommandItem {
  readonly id: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly path?: string;
  readonly action?: () => void;
  readonly keywords?: string;
}

interface CommandGroup {
  readonly heading: string;
  readonly items: readonly CommandItem[];
}

// ---------------------------------------------------------------------------
// Command definitions
// ---------------------------------------------------------------------------

const NAVIGATION_COMMANDS: readonly CommandItem[] = [
  { id: "nav-dashboard", label: "Tableau de bord", icon: LayoutDashboard, path: "/admin", keywords: "accueil dashboard home" },
  { id: "nav-orders", label: "Commandes", icon: ShoppingCart, path: "/admin/orders", keywords: "orders liste" },
  { id: "nav-products", label: "Produits", icon: Package, path: "/admin/products", keywords: "catalogue articles" },
  { id: "nav-clients", label: "Clients 360", icon: Users, path: "/admin/clients", keywords: "customers utilisateurs" },
  { id: "nav-payments", label: "Paiements", icon: CreditCard, path: "/admin/payments", keywords: "transactions money" },
  { id: "nav-logistics", label: "Logistique", icon: Truck, path: "/admin/logistique", keywords: "transport fleet" },
  { id: "nav-deliveries", label: "Tableau des livraisons", icon: MapPin, path: "/admin/deliveries", keywords: "delivery board" },
  { id: "nav-reports", label: "Rapports", icon: FileText, path: "/admin/reports", keywords: "statistiques" },
  { id: "nav-crm", label: "Segments CRM", icon: Heart, path: "/admin/segments", keywords: "segments fidelite" },
  { id: "nav-promotions", label: "Marketing & Promotions", icon: Megaphone, path: "/admin/promotions", keywords: "campagnes promo" },
  { id: "nav-b2b", label: "Clients B2B", icon: Building2, path: "/admin/b2b/customers", keywords: "entreprises wholesale" },
  { id: "nav-ai", label: "Intelligence IA", icon: Sparkles, path: "/admin/ai", keywords: "intelligence artificielle predictions" },
  { id: "nav-finance", label: "Finance & Revenus", icon: DollarSign, path: "/admin/revenue", keywords: "comptabilite chiffre affaires" },
  { id: "nav-analytics", label: "Vue analytique", icon: BarChart3, path: "/admin/analytics", keywords: "charts graphiques" },
  { id: "nav-trending", label: "Tendances", icon: Eye, path: "/admin/analytics/trending", keywords: "trending popular" },
  { id: "nav-content", label: "Gestion du contenu", icon: ScrollText, path: "/admin/contenu", keywords: "cms pages banners" },
  { id: "nav-modules", label: "Param\u00e8tres & Modules", icon: Settings, path: "/admin/modules", keywords: "configuration" },
  { id: "nav-privacy", label: "Vie priv\u00e9e", icon: ShieldCheck, path: "/admin/vie-privee", keywords: "rgpd gdpr donnees" },
  { id: "nav-enterprise", label: "Entreprise", icon: Cog, path: "/admin/entreprise", keywords: "organization" },
  { id: "nav-audit", label: "Journal d'audit", icon: ScrollText, path: "/admin/audit-log", keywords: "logs historique" },
  { id: "nav-operations", label: "Centre des op\u00e9rations", icon: Cog, path: "/admin/operations", keywords: "ops centre" },
  { id: "nav-errors", label: "Tableau des erreurs", icon: AlertTriangle, path: "/admin/errors", keywords: "bugs incidents" },
];

function getActionCommands(navigate: (path: string) => void): readonly CommandItem[] {
  return [
    { id: "act-new-order", label: "Cr\u00e9er une commande", icon: Plus, action: () => navigate("/admin/orders?action=create"), keywords: "nouvelle order" },
    { id: "act-new-product", label: "Ajouter un produit", icon: Plus, action: () => navigate("/admin/products?action=create"), keywords: "nouveau article" },
    { id: "act-new-client", label: "Ajouter un client", icon: Plus, action: () => navigate("/admin/clients?action=create"), keywords: "nouveau customer" },
    { id: "act-new-promo", label: "Cr\u00e9er une promotion", icon: Plus, action: () => navigate("/admin/promotions?action=create"), keywords: "nouvelle campagne" },
    { id: "act-export-orders", label: "Exporter les commandes", icon: FileText, action: () => navigate("/admin/orders?action=export"), keywords: "download csv" },
    { id: "act-export-products", label: "Exporter les produits", icon: FileText, action: () => navigate("/admin/products?action=export"), keywords: "download csv" },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleSelect = useCallback(
    (item: CommandItem) => {
      setOpen(false);
      if (item.action) {
        item.action();
      } else if (item.path) {
        navigate(item.path);
      }
    },
    [navigate],
  );

  const actionCommands = getActionCommands(navigate);

  const groups: readonly CommandGroup[] = [
    { heading: "Navigation", items: NAVIGATION_COMMANDS },
    { heading: "Actions", items: actionCommands },
  ];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="flex items-start justify-center pt-[15vh]">
        <Command
          className="relative w-full max-w-lg overflow-hidden rounded-xl border border-[#e7e0d6] bg-white shadow-2xl"
          label="Palette de commandes"
        >
          {/* Search input */}
          <div className="flex items-center gap-3 border-b border-[#e7e0d6] px-4">
            <Search className="h-4 w-4 shrink-0 text-gray-400" />
            <Command.Input
              placeholder="Rechercher une page ou action..."
              className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
              autoFocus
            />
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-600"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Results */}
          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty className="px-4 py-8 text-center text-sm text-gray-500">
              Aucun r&eacute;sultat trouv&eacute;.
            </Command.Empty>

            {groups.map((group) => (
              <Command.Group
                key={group.heading}
                heading={group.heading}
                className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-gray-400"
              >
                {group.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Command.Item
                      key={item.id}
                      value={`${item.label} ${item.keywords ?? ""}`}
                      onSelect={() => handleSelect(item)}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-gray-700 transition-colors data-[selected=true]:bg-[#FAF6F0] data-[selected=true]:text-[#C75B39]"
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{item.label}</span>
                    </Command.Item>
                  );
                })}
              </Command.Group>
            ))}
          </Command.List>

          {/* Footer hint */}
          <div className="flex items-center justify-between border-t border-[#e7e0d6] px-4 py-2 text-xs text-gray-400">
            <span>Naviguer avec &uarr;&darr; &middot; Entr&eacute;e pour s&eacute;lectionner</span>
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px]">Esc</kbd>
          </div>
        </Command>
      </div>
    </div>
  );
}
